import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { User, Phone, Info, Check } from 'lucide-react';

import BaseModal from '@/shared/components/modals/core/BaseModal.jsx';
import Input from '@/shared/components/elements/Input';
import Select from '@/shared/components/elements/Select';
import Button from '@/shared/components/elements/Button';
import { memberSchema } from '@/feature/Users/schemas/memberSchemas';
import { extractFieldErrors } from '@/shared/utils/errorUtils';

function MemberModal({
  isOpen,
  onClose,
  onSubmit,
  member = null,
  isLoading = false,
}) {
  const [serverErrors, setServerErrors] = useState({});
  const isEditMode = !!member;

  const form = useForm({
    defaultValues: {
      phone_number: member?.phone_number || '',
      role: member?.role || 'viewer',
    },
    validatorAdapter: zodValidator(),
    onSubmit: async ({ value }) => {
      try {
        setServerErrors({});
        await onSubmit({ ...value, member });
        form.reset();
        onClose();
      } catch (error) {
        console.error('Member operation error:', error);
        const fieldErrors = extractFieldErrors(error);
        if (Object.keys(fieldErrors).length > 0) {
          setServerErrors(fieldErrors);
        }
      }
    },
  });

  useEffect(() => {
    if (isOpen && member) {
      form.setFieldValue('phone_number', member.phone_number);
      form.setFieldValue('role', member.role || 'viewer');
    } else if (isOpen && !member) {
      form.reset();
      setServerErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, member]);

  const roleOptions = [
    { value: 'viewer', label: 'بازدیدکننده' },
    { value: 'admin', label: 'مدیر' },
  ];

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'ویرایش کاربر' : 'افزودن کاربر جدید'}
      icon={<User size={20} />}
      iconVariant="primary"
      size="md"
      closeOnBackdropClick={!isLoading}
      closeOnEscape={!isLoading}
      showCloseButton={!isLoading}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            fullWidth
            onClick={onClose}
            disabled={isLoading}
          >
            انصراف
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={isLoading}
            icon={<Check size={18} />}
            iconPosition="left"
            onClick={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
          >
            {isEditMode ? 'ذخیره تغییرات' : 'افزودن کاربر'}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        className="space-y-5 p-6"
      >
        <form.Field
          name="phone_number"
          validators={{
            onChange: isEditMode ? undefined : memberSchema.shape.phone_number,
          }}
        >
          {(field) => {
            const fieldError = field.state.meta.errors?.[0];
            const errorMessage = fieldError
              ? typeof fieldError === 'string'
                ? fieldError
                : fieldError.message || 'خطای اعتبارسنجی'
              : serverErrors.phone_number;

            return (
              <Input
                label="شماره تلفن"
                type="text"
                placeholder="09123456789"
                value={field.state.value}
                onChange={(e) => {
                  field.handleChange(e.target.value);
                  if (serverErrors.phone_number) {
                    setServerErrors((prev) => ({
                      ...prev,
                      phone_number: undefined,
                    }));
                  }
                }}
                error={errorMessage}
                icon={<Phone size={18} />}
                disabled={isLoading || isEditMode}
                maxLength={11}
              />
            );
          }}
        </form.Field>

        <form.Field
          name="role"
          validators={{
            onChange: memberSchema.shape.role,
          }}
        >
          {(field) => {
            const fieldError = field.state.meta.errors?.[0];
            const errorMessage = fieldError
              ? typeof fieldError === 'string'
                ? fieldError
                : fieldError.message || 'خطای اعتبارسنجی'
              : serverErrors.role;

            return (
              <Select
                label="نقش کاربر"
                value={field.state.value}
                onChange={(value) => {
                  field.handleChange(value);
                  if (serverErrors.role) {
                    setServerErrors((prev) => ({
                      ...prev,
                      role: undefined,
                    }));
                  }
                }}
                options={roleOptions}
                error={errorMessage}
                disabled={isLoading}
                placeholder="انتخاب نقش..."
              />
            );
          }}
        </form.Field>

        {!isEditMode && (
          <div className="bg-secondary-6 border-secondary-20 rounded-xl border p-4 backdrop-blur-sm transition-colors duration-300 dark:border-blue-500/30 dark:bg-blue-500/10">
            <div className="flex items-start gap-2">
              <Info
                size={16}
                className="text-secondary-80 mt-0.5 transition-colors duration-300 dark:text-blue-400"
              />
              <p className="text-secondary-100 text-xs leading-relaxed transition-colors duration-300 dark:text-blue-300">
                کاربر با شماره تلفن وارد شده به مرکز شما اضافه خواهد شد.
              </p>
            </div>
          </div>
        )}
      </form>
    </BaseModal>
  );
}

MemberModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  member: PropTypes.object,
  isLoading: PropTypes.bool,
};

export default MemberModal;
